import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { difference } from 'lodash';
import { useTranslation } from 'react-i18next';
import BomTypeGroupCard from '../../../../Components/Card/BomTypeGroupCard/BomTypeGroupCard';
import AddEditBomTypeGroup from './AddEditBomTypeGroup';
import BtnAdd from '../../../../Components/Utility/FilterAction/BtnAdd';
import BtnDelete from '../../../../Components/Utility/FilterAction/BtnDelete';
import BtnSelectAll from '../../../../Components/Utility/FilterAction/BtnSelectAll';
import { fetchBomTypeGroupAction, deleteBomTypeGroupByIdAction } from '../Store/BomType.action';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import Loader from '../../../../Components/Utility/Loader/Loader';
import SearchBar from '../../../../Components/Utility/FilterAction/SearchBar';
import { Page_Size } from '../../../../Components/helper';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import BomTypeTab from '../BomTypeTab';

function BomTypeGroup({ fetchBomTypeGroupAction, deleteBomTypeGroupByIdAction }) {
  const { t } = useTranslation('common');
  const { createConditionalComponent, CheckActionPermission } = useComponent();
  const ProductTestingData = useSelector((redState) => redState);
  const { Users } = ProductTestingData;
  const { logInRights } = Users;
  const [selectedIdArray, setSelectedIdArray] = useState([]);
  const [showBomTypeGroupModal, setBomTypeGroupModal] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [selectedBomId, setBomId] = useState(null);
  const [state, setState] = useState({
    bomTypeGroupList: [],
    pageIndex: 1,
    pageSize: Page_Size,
    hasMore: true,
    loading: true,
    totalRecords: null,
    searchText: '',
    searchLoaded: false,
  });
  const [isSelectAll, setSelectAll] = useState(false);

  const [showSearchMobile, setSearchMobile] = useState(false);

  async function getBomGroupList() {
    const params = {
      pageIndex: state.pageIndex,
      pageSize: state.pageSize,
      bomTypeGroupList: state.bomTypeGroupList,
      totalRecords: state.totalRecords,
    };
    await getBomTypeGroupList(params);
  }

  useEffect(() => {
    getBomGroupList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const AddEditDesignWrapper = createConditionalComponent(logInRights, 'libraries', 'bom', 'addEdit');
  const DeleteDesignWrapper = createConditionalComponent(logInRights, 'libraries', 'bom', 'delete');

  const BomAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'bom', 'addEdit');
  const BomDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'bom', 'delete');
  const inputCheck = !(BomAddEditAccess || BomDeleteAccess);

  const getBomTypeGroupList = async (data) => {
    if (data.totalRecords !== null && data.bomTypeGroupList.length >= data.totalRecords) {
      setState((state) => ({
        ...state,
        hasMore: false,
      }));
      return;
    }

    setState((state) => ({
      ...state,
      loading: true,
    }));
    const params = {
      pageIndex: data.pageIndex,
      pageSize: data.pageSize,
    };
    if (data.searchText) {
      params.searchText = data.searchText;
    }
    const res = await fetchBomTypeGroupAction(params);

    const resDoc = res?.data || null;
    setState((state) => ({
      ...state,
      bomTypeGroupList: resDoc && Object.keys(resDoc).length ? [...data.bomTypeGroupList, ...resDoc.doc] : [],
      pageIndex: data.pageIndex + 1,
      loading: false,
      totalRecords: resDoc && Object.keys(resDoc).length ? resDoc?.totalCount : 0,
    }));
    if (data.isSelectAll) {
      const selectedItems = [...data.selectedIdArray];
      const newGroupIds = resDoc && Object.keys(resDoc).length ? resDoc?.doc.map((item) => item._id) : [];
      setSelectedIdArray([...selectedItems, ...newGroupIds]);
    }
  };

  const refreshPage = async () => {
    setState((state) => ({
      ...state,
      loading: true,
      pageIndex: 1,
      bomTypeGroupList: [],
      totalRecords: null,
      hasMore: true,
      searchText: '',
      searchLoaded: false,
    }));
    setSelectedIdArray([]);
    setBomId(null);
    setSelectAll(false);
    const params = {
      pageIndex: 1,
      pageSize: state.pageSize,
      bomTypeGroupList: [],
      totalRecords: null,
      isSelectAll: false,
      selectedIdArray: [],
    };
    await getBomTypeGroupList(params);
  };

  const handleBomTypePopup = (refresh) => {
    setBomTypeGroupModal(!showBomTypeGroupModal);
    if (!showBomTypeGroupModal) {
      setBomId(null);
    }
    if (refresh) {
      refreshPage();
    }
  };

  const editBomTypeGroup = (id) => {
    setBomId(id);
    setBomTypeGroupModal(!showBomTypeGroupModal);
  };

  const onSelectCheckBox = async (e, id) => {
    let selectedItems = [...selectedIdArray];
    const findItems = selectedItems.filter((item) => item === id);
    if (findItems.length > 0) {
      selectedItems = selectedItems.filter((item) => item !== id);
      setSelectedIdArray([...selectedItems]);
    } else {
      setSelectedIdArray([...selectedItems, id]);
    }
  };

  const deleteBomTypeGroup = (id) => {
    setBomId(id);
    setShowConfirmationPopup(!showConfirmationPopup);
  };

  const deleteById = async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));
    const params = {};
    if (selectedBomId) {
      params.bomTypeGroupId = [selectedBomId];
    } else {
      params.bomTypeGroupId = [...selectedIdArray];
    }
    const res = await deleteBomTypeGroupByIdAction(params, t);
    if (res) {
      setShowConfirmationPopup(!showConfirmationPopup);
      refreshPage();
    } else {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedIdArray([]);
    } else {
      const selectedItems = [...selectedIdArray];
      for (const item of state.bomTypeGroupList) {
        const findItems = selectedItems.filter((val) => val === item._id);
        if (findItems.length === 0) {
          selectedItems.push(item._id);
        }
      }
      setSelectedIdArray(selectedItems);
    }
    setSelectAll(!isSelectAll);
  };

  useEffect(() => {
    if (state.bomTypeGroupList.length > 0) {
      const stateIds = state.bomTypeGroupList.map((item) => item._id);
      const selectedItems = [...selectedIdArray];
      const unSelectedItems = difference(stateIds, selectedItems);
      if (unSelectedItems.length > 0) {
        setSelectAll(false);
      } else {
        setSelectAll(true);
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdArray]);

  const handleSearch = async (e) => {
    const targetValue = e.target.value;
    setState((state) => ({
      ...state,
      searchText: targetValue,
      searchLoaded: true,
    }));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state.searchLoaded) {
        setState((state) => ({
          ...state,
          loading: true,
          pageIndex: 1,
          bomTypeGroupList: [],
          totalRecords: null,
          hasMore: true,
        }));
        setSelectedIdArray([]);
        setBomId(null);
        setSelectAll(false);
        const params = {
          pageIndex: 1,
          pageSize: state.pageSize,
          searchText: state.searchText,
          bomTypeGroupList: [],
          totalRecords: null,
          isSelectAll: false,
          selectedIdArray: [],
        };
        await getBomTypeGroupList(params);
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchText]);

  const handleNextPagesInScroll = async () => {
    const params = {
      pageIndex: state.pageIndex,
      pageSize: state.pageSize,
      searchText: state.searchText,
      bomTypeGroupList: state.bomTypeGroupList,
      totalRecords: state.totalRecords,
      isSelectAll,
      selectedIdArray,
    };
    await getBomTypeGroupList(params);
  };

  const handleMobileSearch = (clear) => {
    if (clear) {
      setState((state) => ({
        ...state,
        searchText: '',
        searchLoaded: true,
      }));
    }
    setSearchMobile(!showSearchMobile);
  };

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container">
          <h4 className="title">{t('BOM_TYPE.BOM_TYPE_GROUP.BOM_TYPE_GROUPS.BOM_TYPE')}</h4>
        </div>
        <div className="search-filter selectall-search-xs">
          <SearchBar
            onSearch={handleSearch}
            value={state.searchText}
            placeholder={t('BOM_TYPE.BOM_TYPE_GROUP.BOM_TYPE_GROUPS.SEARCH_BOM_TYPE_GROUP')}
            showMobileSearch={showSearchMobile}
            handleMobileSearch={handleMobileSearch}
          />
          <AddEditDesignWrapper>
            <BtnAdd onBtnClick={() => handleBomTypePopup()} addEditAccess={BomAddEditAccess} />
          </AddEditDesignWrapper>
          <DeleteDesignWrapper>
            <BtnDelete
              multiDelete={() => {
                deleteBomTypeGroup(null);
              }}
              isDisabled={selectedIdArray?.length <= 0}
            />
          </DeleteDesignWrapper>
          <div className="break" />
          {BomAddEditAccess || BomDeleteAccess ? (
            <BtnSelectAll handleSelectAll={handleSelectAll} isSelectAll={isSelectAll} isDisabled={state?.bomTypeGroupList.length === 0} />
          ) : (
            ''
          )}
        </div>
      </div>
      <div className="container-fluid">
        <BomTypeTab />

        <div className="">
          {!state.loading && state.bomTypeGroupList.length === 0 && (
            <div className="w-100 text-center">
              <h6>{t('BOM_TYPE.BOM_TYPE_GROUP.BOM_TYPE_GROUPS.NO_RECORD_FOUND')}</h6>
            </div>
          )}
        </div>
      </div>

      {(state.bomTypeGroupList !== null || state.bomTypeGroupList.length !== 0) && (
        <InfiniteScroll
          dataLength={state.bomTypeGroupList.length} // This is important field to render the next data
          next={() => handleNextPagesInScroll()}
          hasMore={state.hasMore}
          loader={state.loading && <Loader />}
        >
          <div className="container-fluid">
            <div className="row">
              {state.bomTypeGroupList.map((item, i) => (
                <BomTypeGroupCard
                  onCheck={onSelectCheckBox}
                  item={item}
                  key={item?._id}
                  index={i}
                  onEdit={editBomTypeGroup}
                  inputCheck={inputCheck}
                  AddEditDesignWrapper={AddEditDesignWrapper}
                  DeleteDesignWrapper={DeleteDesignWrapper}
                  onDelete={deleteBomTypeGroup}
                  selectedIds={selectedIdArray}
                  BomAddEditAccess={BomAddEditAccess}
                  BomDeleteAccess={BomDeleteAccess}
                />
              ))}
            </div>
          </div>
        </InfiniteScroll>
      )}

      {showBomTypeGroupModal && (
        <AddEditBomTypeGroup
          title={
            selectedBomId
              ? t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.EDIT_BOM_GROUP_TYPE')
              : t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.ADD_BOM_GROUP_TYPE')
          }
          onOpen={showBomTypeGroupModal}
          id={selectedBomId}
          handleBomTypePopup={handleBomTypePopup}
          toggle={() => setBomTypeGroupModal(!showBomTypeGroupModal)}
        />
      )}

      {showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup}
          onConfirm={deleteById}
          loading={state.loading}
          title={t('CONFIRMATION_POPUP_VIEW.BOM_TYPE_GROUP.TITLE')}
          message={t('CONFIRMATION_POPUP_VIEW.BOM_TYPE_GROUP.DELETE_MESSAGE')}
          toggle={() => setShowConfirmationPopup(!showConfirmationPopup)}
        />
      )}
    </>
  );
}

const mapDispatchToProps = {
  fetchBomTypeGroupAction,
  deleteBomTypeGroupByIdAction,
};

export default connect(null, mapDispatchToProps)(BomTypeGroup);
